"use client";
import { useEffect, useRef } from "react";

import clsx from "clsx";
import { createPortal } from "react-dom";

import CloseSvg from "~/assets/images/modal-close.svg?react";

import styles from "./Modal.module.scss";

export default function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  closeOnBackdrop = true,
  initialFocusRef,
  className,
  panelClassName,
  idPrefix = "modal",
}) {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const lastActiveRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      lastActiveRef.current = document.activeElement;
      const el = initialFocusRef?.current || panelRef.current;
      requestAnimationFrame(() => el?.focus?.());
    } else if (lastActiveRef.current) {
      lastActiveRef.current.focus?.();
    }
  }, [open, initialFocusRef]);

  if (typeof document === "undefined") return null;
  if (!open) return null;

  const handleBackdrop = (e) => {
    if (!closeOnBackdrop) return;
    if (e.target === overlayRef.current) onClose?.();
  };

  const headerId = `${idPrefix}-title`;
  const bodyId = `${idPrefix}-body`;

  return createPortal(
    <div
      ref={overlayRef}
      className={clsx(styles.overlay, !open && styles.hidden, className)}
      onMouseDown={handleBackdrop}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={clsx(styles.panel, panelClassName)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? headerId : undefined}
        aria-describedby={bodyId}
        tabIndex={-1}
      >
        <button aria-label="Close" className={clsx(styles.closeBtn)} onClick={() => onClose?.()}>
          <CloseSvg />
        </button>
        {title && (
          <div id={headerId} className={styles.title}>
            {title}
          </div>
        )}
        <div id={bodyId} className={styles.body}>
          {children}
        </div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
}
